import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { User } from 'lucide-react';
import { useIsUserOnline } from '@/hooks/use-presence';

// Cache in-memory per le immagini caricate
const imageCache = new Map<string, boolean>();

// Interfaccia per le props del componente
interface TelegramImageProps {
  imageUrl?: string;
  photoUrl?: string;
  fileDataBase64?: string | null;
  photoId?: number;
  photos?: any[];
  alt?: string;
  className?: string;
  variant?: 'avatar' | 'rectangle';
  onClick?: () => void;
  username?: string;
  userId?: number;
  style?: React.CSSProperties;
  notificationCount?: number;
  showOnlineIndicator?: boolean;
  isOnline?: boolean;
}

/**
 * COMPONENTE TELEGRAM IMAGE CON DEBUG COMPLETO E ERROR HANDLING
 * Segue esattamente l'approccio di participation-notifications.tsx ma con debug esteso
 */
const TelegramImage: React.FC<TelegramImageProps> = React.memo(({
  fileDataBase64,
  photoUrl,
  imageUrl,
  photoId,
  photos,
  alt,
  className,
  variant = 'rectangle',
  onClick,
  username,
  userId,
  style,
  notificationCount,
  showOnlineIndicator = false,
  isOnline: isOnlineProp
}) => {
  // Hook per stato online (solo se userId è fornito e showOnlineIndicator è true)
  const isOnlineFromHook = useIsUserOnline(userId || 0);
  const isOnline = isOnlineProp ?? (showOnlineIndicator && userId ? isOnlineFromHook : false);

  const [imageError, setImageError] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [isCachedImage, setIsCachedImage] = useState(false);

  // Calcola effectivePhotoUrl con useMemo per evitare ricalcoli inutili
  const effectivePhotoUrl = useMemo(() => {
    let url: string | undefined;

    // Priority 1: photoUrl - per foto profilo/gruppo (MASSIMA PRIORITÀ per profili/gruppi)
  // photoUrl dovrebbe essere usato per foto personali e poster di gruppi
  if (photoUrl) {
    if (typeof photoUrl === 'string' && photoUrl.trim() !== '') {
      url = photoUrl;
    } else if (typeof photoUrl === 'object' && (photoUrl as any)?.url) {
      url = (photoUrl as any).url;
    }
  }

  // Priority 2: imageUrl - fallback per locandine eventi (solo se photoUrl non è impostato)
  // imageUrl è usato principalmente per le locandine degli eventi
  if (!url && imageUrl) {
    if (typeof imageUrl === 'string' && imageUrl.trim() !== '') {
      url = imageUrl;
    } else if (typeof imageUrl === 'object' && (imageUrl as any)?.url) {
      url = (imageUrl as any).url;
    }
  }

  // Priority 3: array photos (solo se imageUrl E photoUrl non sono impostati)
  // 🔧 CRITICAL FIX: Cerca sempre la foto profilo nell'array photos
  if (!url && photos && Array.isArray(photos) && photos.length > 0) {
    // 🔧 FIX: Ordina foto per mostrare SEMPRE la foto profilo prima
    const sortedPhotos = [...photos].sort((a: any, b: any) => {
      // Verifica isProfilePhoto con gestione di vari tipi (boolean, number, string)
      const aIsProfile = !!(a.isProfilePhoto === true || a.isProfilePhoto === 1 || a.isProfilePhoto === '1');
      const bIsProfile = !!(b.isProfilePhoto === true || b.isProfilePhoto === 1 || b.isProfilePhoto === '1');

      // Foto profilo viene prima
      if (aIsProfile && !bIsProfile) return -1;
      if (!aIsProfile && bIsProfile) return 1;

      // Poi ordina per campo "order"
      if (a.order !== undefined && b.order !== undefined && a.order !== b.order) {
        return (a.order || 0) - (b.order || 0);
      }

      // Mantieni ordine originale se non c'è order
      return 0;
    });

    const firstPhoto = sortedPhotos[0];
    let photoUrl = null;

    // 🔧 IMPROVED: Try multiple extraction methods
    if (typeof firstPhoto === 'object' && firstPhoto !== null) {
      // Try direct URL field
      if (firstPhoto.url && typeof firstPhoto.url === 'string') {
        photoUrl = firstPhoto.url;
      }
      // Try fileDataBase64
      else if (firstPhoto.fileDataBase64 && typeof firstPhoto.fileDataBase64 === 'string') {
        const base64Data = firstPhoto.fileDataBase64.startsWith('data:') 
          ? firstPhoto.fileDataBase64 
          : `data:image/jpeg;base64,${firstPhoto.fileDataBase64}`;
        photoUrl = base64Data;
      }
      // Try generating URL from photo ID
      else if (firstPhoto.id && userId) {
        photoUrl = `/api/images/users/${userId}/photos/${firstPhoto.id}`;
      }
    } else if (typeof firstPhoto === 'string') {
      photoUrl = firstPhoto;
    } else if (typeof firstPhoto === 'number') {
      if (userId) {
        photoUrl = `/api/images/users/${userId}/photos/${firstPhoto}`;
      } else {
        console.error(`[TelegramImage] ❌ Photo ID ${firstPhoto} but userId missing for ${username}`);
      }
    }

    if (photoUrl && typeof photoUrl === 'string') {
      // Decode URL if needed (but not base64)
      if (photoUrl.includes('%') && !photoUrl.startsWith('data:')) {
        try {
          photoUrl = decodeURIComponent(photoUrl);
        } catch (e) {
          console.warn(`[TelegramImage] ⚠️ Errore decodifica URL per ${username}:`, e);
        }
      }
    }

    if (photoUrl && typeof photoUrl === 'string' && photoUrl.trim() !== '' && photoUrl !== '[object]') {
      url = photoUrl;
    }
  }

  // Priority 4: fileDataBase64
  if (!url && fileDataBase64 && fileDataBase64.trim() !== '') {
    const base64Data = fileDataBase64.startsWith('data:') ? fileDataBase64 : `data:image/jpeg;base64,${fileDataBase64}`;
    url = base64Data;
  }


    return url;
  }, [photoUrl, imageUrl, photos, fileDataBase64, userId, username]);

  // Inizializza isLoaded controllando se l'immagine è già in cache
  const [isLoaded, setIsLoaded] = useState(() => {
    return effectivePhotoUrl ? imageCache.has(effectivePhotoUrl) : false;
  });

  // Calcola finalSrc una volta - garantisce che sia una stringa
  const finalSrc = effectivePhotoUrl || '';

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (!img) {
      setImageError(true);
      return;
    }

    setLoadAttempts(prev => prev + 1);

    if (loadAttempts >= 1) {
      setImageError(true);
      return;
    }

    setTimeout(() => {
      if (!e.currentTarget) {
        setImageError(true);
        return;
      }
      if (e.currentTarget.src.startsWith('/api/')) {
        e.currentTarget.src = `${window.location.origin}${e.currentTarget.src}`;
      } else {
        e.currentTarget.src = finalSrc;
      }
    }, 200);
  };

  const handleImageLoad = (e?: React.SyntheticEvent<HTMLImageElement>) => {
    setImageError(false);
    setLoadAttempts(0);
    setIsLoaded(true);
    
    // Controlla se l'immagine era già in cache del browser
    if (e?.currentTarget && e.currentTarget.complete && e.currentTarget.naturalHeight !== 0) {
      setIsCachedImage(true);
    }
    
    // Aggiungi alla cache
    if (effectivePhotoUrl) {
      imageCache.set(effectivePhotoUrl, true);
    }
  };

  // FALLBACK: Placeholder colorato con supporto per nuovo sistema photos
  const renderPlaceholder = () => {
    const variantClasses = variant === 'avatar' ? 'rounded-full' : 'rounded-md';

    // Per avatar, usa skeleton grigio scuro
    if (variant === 'avatar') {
      return (
        <div className="relative inline-block w-full h-full">
          <div
            className={`flex items-center justify-center ${variantClasses} w-full h-full aspect-square ${className?.includes('w-') || className?.includes('h-') ? '' : 'min-w-full min-h-full'} relative animate-pulse`}
            style={{
              ...style,
              width: style?.width || '100%',
              height: style?.height || '100%',
              backgroundColor: '#252525'
            }}
            onClick={onClick}
          >
            {/* Skeleton - nessun contenuto visibile */}

            {/* Online Indicator - Top right corner - DENTRO il div */}
            {showOnlineIndicator && isOnline && (
              <div
                className="absolute h-3 w-3 rounded-full bg-green-500 border-2 border-white animate-pulse"
                style={{
                  top: '-2px',
                  right: '-2px',
                  zIndex: 10
                }}
              />
            )}
          </div>

          {/* Notification Badge - se presente */}
          {notificationCount !== undefined && notificationCount > 0 && (
            <div
              className="absolute bg-red-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
              style={{
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
                height: '100%',
                fontSize: '18px',
                zIndex: 99999,
                lineHeight: '1',
                boxShadow: '0 4px 12px rgba(255,0,0,0.4)',
                pointerEvents: 'none'
              }}
            >
              {notificationCount > 9 ? '9+' : notificationCount}
            </div>
          )}
        </div>
      );
    }

    // Per rectangle, mantieni il comportamento originale con colori e lettera
    let firstLetter = '?';
    if (username && typeof username === 'string' && username.trim().length > 0) {
      const cleanUsername = username.trim();
      const firstChar = cleanUsername.charAt(0).toUpperCase();
      if (/^[A-Za-z]$/.test(firstChar)) {
        firstLetter = firstChar;
      }
    }

    const backgroundColors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-red-500', 'bg-yellow-500', 'bg-teal-500'
    ];
    const colorIndex = username ? Math.abs(username.charCodeAt(0)) % backgroundColors.length : 0;
    const bgColor = backgroundColors[colorIndex];

    return (
      <div className="relative inline-block w-full h-full">
        <div
          className={`flex items-center justify-center text-white font-semibold ${bgColor} ${variantClasses} w-full h-full aspect-square ${className?.includes('w-') || className?.includes('h-') ? '' : 'min-w-full min-h-full'} relative`}
          style={{ ...style, width: style?.width || '100%', height: style?.height || '100%' }}
          onClick={onClick}
        >
          <span className="text-2xl font-bold">{firstLetter}</span>

          {/* Online Indicator - Top right corner - DENTRO il div */}
          {showOnlineIndicator && isOnline && (
            <div
              className="absolute h-3 w-3 rounded-full bg-green-500 border-2 border-white animate-pulse"
              style={{
                top: '-2px',
                right: '-2px',
                zIndex: 10
              }}
            />
          )}
        </div>

        {/* Notification Badge - se presente */}
        {notificationCount !== undefined && notificationCount > 0 && (
          <div
            className="absolute bg-red-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              fontSize: '18px',
              zIndex: 99999,
              lineHeight: '1',
              boxShadow: '0 4px 12px rgba(255,0,0,0.4)',
              pointerEvents: 'none'
            }}
          >
            {notificationCount > 9 ? '9+' : notificationCount}
          </div>
        )}
      </div>
    );
  };

  if (!effectivePhotoUrl || effectivePhotoUrl.trim() === '' || imageError) {
    return renderPlaceholder();
  }

  if (effectivePhotoUrl === '0' || effectivePhotoUrl === 'undefined' || effectivePhotoUrl === 'null') {
    return renderPlaceholder();
  }

  // finalSrc è già definita sopra (effectivePhotoUrl || '')
  // Controlla se l'immagine è già in cache
  const isCached = imageCache.has(finalSrc);

  const variantClasses = variant === 'avatar'
    ? 'rounded-full overflow-hidden'
    : 'rounded-md overflow-hidden';

  return (
    <div className="relative inline-block">


      <div
        className={`${variantClasses} ${className} ${onClick ? 'cursor-pointer' : ''} aspect-square w-12 h-12`}
        onClick={onClick}
        style={{ ...style, position: 'relative' }}
      >
        <img
          src={finalSrc}
          alt={alt || `Foto di ${username || 'utente'}`}
          className={cn(
            "w-full h-full object-cover",
            // Rimuovi transizione per immagini cached/già caricate
            isCached || isCachedImage ? "" : "transition-opacity duration-300",
            !isLoaded && !isCached ? "opacity-0" : "opacity-100"
          )}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading={isCached ? "eager" : "lazy"}
          decoding={isCached ? "sync" : "async"}
          crossOrigin="use-credentials"
          style={{ minHeight: '100%', minWidth: '100%' }}
          // Controlla se l'immagine è già caricata nel browser al mount
          ref={(img) => {
            if (img && img.complete && img.naturalHeight !== 0 && !isLoaded) {
              setIsLoaded(true);
              setIsCachedImage(true);
              if (effectivePhotoUrl) {
                imageCache.set(effectivePhotoUrl, true);
              }
            }
          }}
        />

        {/* Online Indicator - Top right corner */}
        {showOnlineIndicator && isOnline && (
          <div
            className="absolute h-3 w-3 rounded-full bg-green-500 border-2 border-white animate-pulse"
            style={{
              top: '-2px',
              right: '-2px',
              zIndex: 10
            }}
          />
        )}

        {/* Notification Badge - Centered and covering the entire avatar */}
        {notificationCount !== undefined && notificationCount > 0 && (
          <div
            className="absolute bg-red-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              fontSize: '18px',
              zIndex: 5,
              lineHeight: '1',
              boxShadow: '0 4px 12px rgba(255,0,0,0.4)',
              pointerEvents: 'none',
              backgroundColor: 'rgba(239, 68, 68, 0.9)' // More opaque red
            }}
          >
            {notificationCount > 9 ? '9+' : notificationCount}
          </div>
        )}
      </div>
    </div>
  );
});

TelegramImage.displayName = 'TelegramImage';

export { TelegramImage };
export default TelegramImage;
